/**
 * Пользовательские настройки MartinUI (DA-15900).
 *
 * Источник истины — атрибут пользователя в Cognito: настройки следуют за человеком
 * на любом браузере и устройстве. localStorage — кэш: панель отрисовывается мгновенно,
 * до ответа Amplify, и работает при недоступности Cognito.
 *
 * Разрешение расхождений — Cognito всегда побеждает: пришли атрибуты, перезаписали кэш.
 * Гонки нет, запись идёт только из явного действия пользователя.
 */

import { Auth } from 'aws-amplify';

const STORAGE_KEY = 'martinui_user_settings';

// Атрибут Cognito, в котором лежат настройки. Одно «ведро» с JSON внутри, а не
// атрибут на каждую настройку: раздел настроек будет расти, а custom-атрибут
// нельзя ни удалить, ни переименовать — плодить их накладно навсегда.
//
// ВРЕМЕННО: занят стандартный profile вместо custom:ui_settings (DA-15900).
// Права cognito-idp:AddCustomAttributes на пуле eu-west-1_lCBt4yFuB у нас нет,
// заведение своего атрибута — заявка владельцу пула. profile в пуле свободен
// (у всех пользователей пуст) и не участвует в маппинге Google IdP (там только
// email и username), так что значение ничем не затирается.
// Когда custom:ui_settings появится — поменять эту строку и больше ничего:
// имя атрибута нигде не дублируется, а пустой атрибут читается как «настроек ещё
// нет» с мягким переходом на кэш, поэтому миграция не нужна.
export const SETTINGS_ATTRIBUTE = 'profile';

// Версия схемы настроек. Поднимать при несовместимом изменении формата —
// тогда старое значение отбрасывается и пользователь получает дефолт.
export const SETTINGS_SCHEMA_VERSION = 1;

export const DEFAULT_USER_SETTINGS = {
    v: SETTINGS_SCHEMA_VERSION,
    // id групп слоёв, скрытых из левой панели (src/constants/layers.js, allLayersGroups)
    hiddenLayerGroups: [],
};

/**
 * Приводит произвольное значение к валидным настройкам.
 * Неизвестные ключи отбрасываются, битые поля заменяются дефолтом: настройки приходят
 * из внешнего хранилища и могли быть записаны другой версией приложения.
 * @param {*} raw - разобранный JSON настроек
 * @returns {Object} валидные настройки
 */
export const normalizeUserSettings = raw => {
    if (!raw || typeof raw !== 'object' || raw.v !== SETTINGS_SCHEMA_VERSION) {
        return DEFAULT_USER_SETTINGS;
    }

    const hiddenLayerGroups = Array.isArray(raw.hiddenLayerGroups)
        ? [ ...new Set(raw.hiddenLayerGroups.filter(id => Number.isInteger(id))) ]
        : [];

    return { v: SETTINGS_SCHEMA_VERSION, hiddenLayerGroups };
};

/**
 * Читает настройки из localStorage
 * @returns {Object} настройки, при отсутствии или ошибке — DEFAULT_USER_SETTINGS
 */
export const readCachedUserSettings = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? normalizeUserSettings(JSON.parse(saved)) : DEFAULT_USER_SETTINGS;
    } catch (err) {
        console.warn('[UserSettings] Error reading from localStorage:', err);
        return DEFAULT_USER_SETTINGS;
    }
};

/**
 * Пишет настройки в localStorage
 * @param {Object} settings - настройки
 */
export const writeCachedUserSettings = settings => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeUserSettings(settings)));
    } catch (err) {
        // localStorage недоступен (приватный режим и т.п.) — молча игнорируем
        console.warn('[UserSettings] Error writing to localStorage:', err);
    }
};

/**
 * Скрыта ли группа слоёв
 * @param {Object} settings - настройки
 * @param {number} groupId - id группы
 * @returns {boolean}
 */
export const isLayerGroupHidden = (settings, groupId) => Boolean(settings)
    && Array.isArray(settings.hiddenLayerGroups)
    && settings.hiddenLayerGroups.includes(groupId);

/**
 * Разбирает настройки из атрибутов пользователя Cognito
 * @param {Object} attributes - атрибуты пользователя (Auth.currentAuthenticatedUser)
 * @returns {Object|null} настройки или null, если атрибут пуст либо непригоден
 */
export const parseSettingsAttribute = attributes => {
    const raw = attributes && attributes[SETTINGS_ATTRIBUTE];
    if (!raw) return null;

    try {
        return normalizeUserSettings(JSON.parse(raw));
    } catch (err) {
        console.warn('[UserSettings] Error parsing Cognito attribute:', err);
        return null;
    }
};

/**
 * Пишет настройки в Cognito и в кэш.
 * Кэш пишется всегда, даже если Cognito ответил ошибкой: настройка не должна
 * теряться из-за сети — на этом устройстве она останется рабочей.
 * @param {Object} settings - настройки
 * @returns {Promise<Object>} { settings, error } — error заполнен, если Cognito не принял
 */
export const saveUserSettings = async settings => {
    const normalized = normalizeUserSettings(settings);
    writeCachedUserSettings(normalized);

    try {
        // Здесь bypassCache не нужен: для записи важен сам объект пользователя,
        // а не актуальность его атрибутов
        const user = await Auth.currentAuthenticatedUser();
        await Auth.updateUserAttributes(user, {
            [SETTINGS_ATTRIBUTE]: JSON.stringify(normalized),
        });
        return { settings: normalized, error: null };
    } catch (error) {
        console.warn('[UserSettings] Error saving to Cognito:', error);
        return { settings: normalized, error };
    }
};
